import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, ArrowLeft, Check, Loader2, Sparkles, MapPin, Stethoscope, UserPlus, Search, Send } from 'lucide-react';
import { api } from '../../lib/api';
import { getReferral } from '../../lib/referral';
import './MarketerOnboarding.css';

// Invite-only marketer onboarding: three short steps, no ID check, no
// address. Creates the marketer organization (type 'marketer' — the server
// insists on the admin's invite stamp) and saves what they tell us about
// where and what they refer, so the concierge starts with their defaults.

const AREAS = [
  'Miami', 'Fort Lauderdale', 'West Palm Beach', 'Orlando', 'Tampa', 'Jacksonville',
  'Houston', 'Dallas', 'San Antonio', 'Austin', 'El Paso', 'Rio Grande Valley',
];
const SPECIALTIES = [
  ['chiro', 'Chiropractic'], ['imaging', 'MRI / imaging'], ['ortho', 'Orthopedics'],
  ['pt', 'Physical therapy'], ['neuro', 'Neurology'], ['pain', 'Pain management'],
];
const TOTAL = 3;

function Confetti() {
  const bits = useMemo(() => Array.from({ length: 28 }, (_, i) => ({
    left: `${(i * 37) % 100}%`, delay: `${(i % 7) * 0.08}s`, bg: ['#0DA7CA', '#223368', '#F59E0B', '#10B981', '#8B5CF6'][i % 5],
  })), []);
  return <div className="mob-confetti">{bits.map((b, i) => <i key={i} style={{ left: b.left, background: b.bg, animationDelay: b.delay }} />)}</div>;
}

export default function MarketerOnboarding({ firstName, lastName, baaVersion, onDone }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [areas, setAreas] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [, setOrgId] = useState(null);

  useEffect(() => {
    if (!name && (firstName || lastName)) setName(`${[firstName, lastName].filter(Boolean).join(' ')} Referrals`);
  }, [firstName, lastName]); // eslint-disable-line

  const toggle = (list, set, v) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const create = async () => {
    setBusy(true); setError('');
    try {
      const ref = getReferral();
      const resp = await api.createLawFirm({
        type: 'marketer', name: name.trim(), phone: phone.trim() || null,
        referral_source: ref?.code || null, referral_meta: ref || null, baa_version: baaVersion,
      });
      setOrgId(resp.org_id);
      // What they refer and where — their concierge defaults. Best effort.
      api.updateOrg(resp.org_id, {
        specialties: specs.map((k) => SPECIALTIES.find(([key]) => key === k)?.[1]).filter(Boolean),
        description: areas.length ? `Refers patients around ${areas.join(', ')}.` : null,
      }).catch(() => {});
      setStep(3);
    } catch (e) {
      setError(e.message || 'Could not create your account');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mob">
      <div className="mob-card" key={step}>
        {step < 3 && (
          <div className="mob-progress">{Array.from({ length: TOTAL }, (_, i) => <span key={i} className={i <= step ? 'on' : ''} />)}</div>
        )}

        {step === 0 && (
          <>
            <div className="mob-eyebrow"><span className="mob-dot" /> Invitation accepted</div>
            <h1>Welcome aboard{firstName ? `, ${firstName}` : ''}. <em>Let&apos;s get you referring.</em></h1>
            <p className="lead">
              You&apos;re joining as a marketer: the person who always knows someone who needs care. MediLink gives you a case file for every patient,
              a concierge that finds the right clinic in seconds, and a referral the clinic has one hour to accept. Free, always.
            </p>
            <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 6 }}>
              <span className="mob-stat"><b>60s</b> to set up</span>
              <span className="mob-stat"><b>1 hr</b> clinic acceptance window</span>
              <span className="mob-stat"><b>$0</b> for you, ever</span>
            </div>
            <div className="mob-actions">
              <span />
              <button className="mob-btn primary" onClick={() => setStep(1)}>Let&apos;s go <ArrowRight size={15} /></button>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div className="mob-eyebrow"><Sparkles size={12} /> Step 1 of 3</div>
            <h1>What should clinics <em>call you</em>?</h1>
            <p className="lead">This is the name a clinic sees when your referral lands in their inbox. Your business name, or just yours.</p>
            {error && <div className="mob-error">{error}</div>}
            <div className="mob-field">
              <label>Name on your referrals</label>
              <input className="mob-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Alicia Kirschner Referrals" autoFocus />
            </div>
            <div className="mob-field">
              <label>Phone <span style={{ fontWeight: 400, opacity: .7 }}>(optional, so clinics can reach you)</span></label>
              <input className="mob-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(305) 555-0100" inputMode="tel" />
            </div>
            <div className="mob-actions">
              <button className="mob-btn ghost" onClick={() => setStep(0)}><ArrowLeft size={14} /> Back</button>
              <button className="mob-btn primary" disabled={!name.trim()} onClick={() => setStep(2)}>Next <ArrowRight size={15} /></button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="mob-eyebrow"><Sparkles size={12} /> Step 2 of 3</div>
            <h1>Where do your patients <em>usually need care</em>?</h1>
            <p className="lead">Pick what fits. The concierge starts from these when you look for a clinic. You can skip this.</p>
            {error && <div className="mob-error">{error}</div>}
            <div className="mob-field">
              <label><MapPin size={11} style={{ verticalAlign: '-1px' }} /> Areas</label>
              <div className="mob-chips">
                {AREAS.map((a) => <button key={a} type="button" className={`mob-chip ${areas.includes(a) ? 'on' : ''}`} onClick={() => toggle(areas, setAreas, a)}>{areas.includes(a) && <Check size={12} />}{a}</button>)}
              </div>
            </div>
            <div className="mob-field">
              <label><Stethoscope size={11} style={{ verticalAlign: '-1px' }} /> Care you refer most</label>
              <div className="mob-chips">
                {SPECIALTIES.map(([k, l]) => <button key={k} type="button" className={`mob-chip ${specs.includes(k) ? 'on' : ''}`} onClick={() => toggle(specs, setSpecs, k)}>{specs.includes(k) && <Check size={12} />}{l}</button>)}
              </div>
            </div>
            <div className="mob-actions">
              <button className="mob-btn ghost" onClick={() => setStep(1)}><ArrowLeft size={14} /> Back</button>
              <button className="mob-btn primary" disabled={busy} onClick={create}>
                {busy ? <><Loader2 size={15} className="animate-spin" /> Setting you up…</> : <>{areas.length || specs.length ? 'Finish' : 'Skip and finish'} <ArrowRight size={15} /></>}
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <Confetti />
            <div className="mob-eyebrow"><Check size={12} /> You&apos;re in</div>
            <h1>Done. <em>{name.trim()}</em> is live.</h1>
            <p className="lead">Three things, and your first referral is out the door. Most people do all three in under five minutes.</p>
            <div className="mob-next">
              <a href="/app/contacts" onClick={(e) => { e.preventDefault(); onDone('/app/contacts'); }}>
                <div className="n"><UserPlus size={14} /></div><div className="t">Add your first client</div><div className="d">Name and phone is enough. Email is optional.</div>
              </a>
              <a href="/app/referrals/concierge" onClick={(e) => { e.preventDefault(); onDone('/app/referrals/concierge'); }}>
                <div className="n"><Search size={14} /></div><div className="t">Find a provider</div><div className="d">Tell the concierge what they need and where.</div>
              </a>
              <a href="/app/referrals" onClick={(e) => { e.preventDefault(); onDone('/app/referrals'); }}>
                <div className="n"><Send size={14} /></div><div className="t">Send the referral</div><div className="d">The clinic has one hour to accept. You&apos;ll know the moment it does.</div>
              </a>
            </div>
            <div className="mob-actions">
              <span className="mob-stat">Free, always. Nothing to pay.</span>
              <button className="mob-btn primary" onClick={() => onDone('/app/dashboard')}>Open my dashboard <ArrowRight size={15} /></button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
